import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fsmApi } from '../../api/fsm';

// Async thunks
export const fetchFSMs = createAsyncThunk(
  'fsm/fetchFSMs',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await fsmApi.listFSMs(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchFSM = createAsyncThunk(
  'fsm/fetchFSM',
  async (fsmId, { rejectWithValue }) => {
    try {
      const response = await fsmApi.getFSM(fsmId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createFSM = createAsyncThunk(
  'fsm/createFSM',
  async (fsmData, { rejectWithValue }) => {
    try {
      const response = await fsmApi.createFSM(fsmData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateFSM = createAsyncThunk(
  'fsm/updateFSM',
  async ({ fsmId, updates }, { rejectWithValue }) => {
    try {
      const response = await fsmApi.updateFSM(fsmId, updates);
      return { fsmId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteFSM = createAsyncThunk(
  'fsm/deleteFSM',
  async (fsmId, { rejectWithValue }) => {
    try {
      await fsmApi.deleteFSM(fsmId);
      return fsmId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  // FSM list
  fsms: [],
  fsmsLoading: false,
  fsmsError: null,
  
  // Current FSM (for editing)
  currentFSM: null,
  currentFSMLoading: false,
  currentFSMError: null,
  
  // Filters and pagination
  filters: {
    limit: 50,
    offset: 0,
    status: '',
    tags: [],
    complexity: ''
  },
  
  // UI state
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  
  // Success messages
  successMessage: null
};

const fsmSlice = createSlice({
  name: 'fsm',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentFSM: (state) => {
      state.currentFSM = null;
      state.currentFSMError = null;
      state.currentFSMLoading = false;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearErrors: (state) => {
      state.fsmsError = null;
      state.currentFSMError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch FSMs
      .addCase(fetchFSMs.pending, (state) => {
        state.fsmsLoading = true;
        state.fsmsError = null;
      })
      .addCase(fetchFSMs.fulfilled, (state, action) => {
        state.fsmsLoading = false;
        state.fsms = action.payload.fsms || action.payload;
      })
      .addCase(fetchFSMs.rejected, (state, action) => {
        state.fsmsLoading = false;
        state.fsmsError = action.payload;
      })
      
      // Fetch single FSM
      .addCase(fetchFSM.pending, (state) => {
        state.currentFSMLoading = true;
        state.currentFSMError = null;
      })
      .addCase(fetchFSM.fulfilled, (state, action) => {
        state.currentFSMLoading = false;
        state.currentFSM = action.payload.fsm || action.payload;
      })
      .addCase(fetchFSM.rejected, (state, action) => {
        state.currentFSMLoading = false;
        state.currentFSMError = action.payload;
      })
      
      // Create FSM
      .addCase(createFSM.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createFSM.fulfilled, (state) => {
        state.isCreating = false;
        state.successMessage = 'State machine created successfully!';
      })
      .addCase(createFSM.rejected, (state, action) => {
        state.isCreating = false;
        state.fsmsError = action.payload;
      })
      
      // Update FSM
      .addCase(updateFSM.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateFSM.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.successMessage = 'State machine updated successfully!';
        
        // Update in fsms list
        const index = state.fsms.findIndex(f => f.fsmId === action.payload.fsmId);
        if (index !== -1) {
          const { visual, ...metadata } = action.payload;
          state.fsms[index] = { ...state.fsms[index], ...metadata };
        }
        
        // Update current FSM metadata but preserve local visual state
        if (state.currentFSM?.fsmId === action.payload.fsmId) {
          const { visual, ...metadata } = action.payload;
          state.currentFSM = { ...state.currentFSM, ...metadata };
        }
      })
      .addCase(updateFSM.rejected, (state, action) => {
        state.isUpdating = false;
        state.currentFSMError = action.payload;
      })
      
      // Delete FSM
      .addCase(deleteFSM.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteFSM.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'State machine deleted successfully!';
        
        // Remove from fsms list
        state.fsms = state.fsms.filter(f => f.fsmId !== action.payload);
        
        // Clear current FSM if it matches
        if (state.currentFSM?.fsmId === action.payload) {
          state.currentFSM = null;
        }
      })
      .addCase(deleteFSM.rejected, (state, action) => {
        state.isDeleting = false;
        state.fsmsError = action.payload;
      });
  }
});

export const { setFilters, clearCurrentFSM, clearSuccessMessage, clearErrors } = fsmSlice.actions;

export default fsmSlice.reducer;
